import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ProviderConfig, ProviderType } from '@/api/types';

export interface ProviderConfigForm {
  provider_type: string;
  display_name: string;
  client_id: string;
  client_secret: string;
  enabled: boolean;
}

interface Props {
  mode: 'create' | 'edit';
  open: boolean;
  initial?: ProviderConfig | null;
  types: ProviderType[];
  pending?: boolean;
  onClose: () => void;
  onSubmit: (values: ProviderConfigForm) => void;
}

const createSchema = z.object({
  provider_type: z.string().min(1, 'Pick a provider type'),
  display_name: z.string().trim().min(1, 'Required').max(128),
  client_id: z.string().trim().min(1, 'Required').max(512),
  client_secret: z.string().min(1, 'Required'),
  enabled: z.boolean(),
});

const editSchema = z.object({
  provider_type: z.string(),
  display_name: z.string().trim().min(1, 'Required').max(128),
  client_id: z.string().trim().min(1, 'Required').max(512),
  // Empty string is allowed on edit and signals "leave secret alone".
  client_secret: z.string().optional().default(''),
  enabled: z.boolean(),
});

export function ProviderConfigDialog({
  mode,
  open,
  initial,
  types,
  pending,
  onClose,
  onSubmit,
}: Props) {
  const schema = mode === 'create' ? createSchema : editSchema;
  const [secretFocused, setSecretFocused] = useState(false);

  const form = useForm<ProviderConfigForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      provider_type: initial?.provider_type ?? types[0]?.type ?? '',
      display_name: initial?.display_name ?? '',
      client_id: initial?.client_id ?? '',
      client_secret: '',
      enabled: initial?.enabled ?? true,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        provider_type: initial?.provider_type ?? types[0]?.type ?? '',
        display_name: initial?.display_name ?? '',
        client_id: initial?.client_id ?? '',
        client_secret: '',
        enabled: initial?.enabled ?? true,
      });
      setSecretFocused(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial, types]);

  const isEdit = mode === 'edit';
  const showSecretPlaceholder = isEdit && initial?.has_client_secret && !secretFocused && !form.watch('client_secret');

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} fullWidth maxWidth="sm">
      <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <DialogTitle>{isEdit ? 'Edit provider config' : 'New provider config'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Controller
              name="provider_type"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth disabled={isEdit} error={!!fieldState.error}>
                  <InputLabel>Provider type</InputLabel>
                  <Select label="Provider type" {...field}>
                    {types.map((t) => (
                      <MenuItem key={t.type} value={t.type}>
                        {t.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {isEdit && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Type is immutable once created.
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            <TextField
              label="Display name"
              fullWidth
              {...form.register('display_name')}
              error={!!form.formState.errors.display_name}
              helperText={form.formState.errors.display_name?.message}
            />

            <TextField
              label="Client ID"
              fullWidth
              {...form.register('client_id')}
              error={!!form.formState.errors.client_id}
              helperText={form.formState.errors.client_id?.message}
              slotProps={{
                htmlInput: { autoCapitalize: 'off', spellCheck: false },
              }}
            />

            <TextField
              label="Client secret"
              fullWidth
              type="password"
              {...form.register('client_secret')}
              onFocus={() => setSecretFocused(true)}
              onBlur={() => setSecretFocused(false)}
              placeholder={showSecretPlaceholder ? '•••• set' : ''}
              error={!!form.formState.errors.client_secret}
              helperText={
                form.formState.errors.client_secret?.message ??
                (isEdit
                  ? 'Leave blank to keep the stored secret unchanged.'
                  : 'Stored encrypted; never returned by the API.')
              }
              slotProps={{
                htmlInput: { autoComplete: 'new-password', spellCheck: false },
              }}
            />

            <Controller
              name="enabled"
              control={form.control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Enabled (users can connect with this provider)"
                />
              )}
            />

            {isEdit && (
              <Alert severity="info">
                Rotating the secret will not invalidate existing user connections, but new connection flows will use it.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={pending}>
            {pending ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
